import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	alpha,
	Box,
	Divider,
	ListItemText,
	Menu,
	MenuItem,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import React, { useState } from "react";

const STAGING_DOMAIN = "staging.artemisrec.com";
const CHECKOUTS = [1, 2, 3, 4, 5];

interface EnvironmentTarget {
	/** Short label shown in the bar, e.g. "PROD", "local 2", "staging 4". */
	label: string;
	origin: string;
	group: "prod" | "local" | "staging";
	/** Set on staging entries so the current instance can be matched by its
	 * STAGING_ENV rather than by hostname — aliases never match an origin. */
	stagingEnv?: string;
}

const TARGETS: EnvironmentTarget[] = [
	{ label: "PROD", origin: "https://odi.artemisrec.com", group: "prod" },
	...CHECKOUTS.map((n) => ({
		label: `local ${n}`,
		origin: `http://localhost:530${n}`,
		group: "local" as const,
	})),
	...CHECKOUTS.map((n) => ({
		label: `staging ${n}`,
		origin: `https://odi${n}.${STAGING_DOMAIN}`,
		group: "staging" as const,
		stagingEnv: `s${n}`,
	})),
];

const GROUP_TITLES: Record<EnvironmentTarget["group"], string> = {
	prod: "Production",
	local: "Dev checkouts",
	staging: "Staging",
};

interface EnvironmentSwitcherProps {
	/** "s1".."s5" when this instance is staging; from /api/v2/system-info/. */
	stagingEnv?: string;
	/** Every hostname this instance answers on, aliases included. */
	stagingHostnames?: string[];
}

/**
 * One chip in the navbar naming the system you are on, opening a menu that
 * switches to any other one at the same path. Six flat chips did not scale to
 * five staging instances, and an alias like jobylon.staging.artemisrec.com
 * gives no clue which instance is serving it — hence the explicit label and
 * the hostname list for the current instance.
 */
export function EnvironmentSwitcher({
	stagingEnv,
	stagingHostnames,
}: EnvironmentSwitcherProps): React.ReactElement {
	const theme = useTheme();
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);

	const current =
		TARGETS.find((t) => t.stagingEnv && t.stagingEnv === stagingEnv) ??
		TARGETS.find((t) => t.origin === window.location.origin);
	const isProd = current?.group === "prod";
	const label = current?.label ?? window.location.host;
	const names = stagingHostnames ?? [];

	const hrefFor = (origin: string) =>
		`${origin}${window.location.pathname}${window.location.search}`;

	return (
		<>
			<Tooltip
				title={
					names.length > 0
						? `Answers on: ${names.join(", ")}`
						: "Switch environment"
				}
			>
				<Box
					component="button"
					onClick={(e: React.MouseEvent<HTMLElement>) =>
						setAnchor(e.currentTarget)
					}
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.25,
						px: 1.25,
						py: 0.5,
						border: 0,
						borderRadius: 1,
						bgcolor: isProd
							? theme.palette.error.main
							: alpha(theme.palette.common.white, 0.35),
						fontFamily: "monospace",
						fontSize: "0.85rem",
						fontWeight: 700,
						color: alpha(theme.palette.common.white, 0.95),
						whiteSpace: "nowrap",
						cursor: "pointer",
					}}
					data-component="environment-switcher"
				>
					{label}
					<ExpandMoreIcon sx={{ fontSize: "1rem" }} />
				</Box>
			</Tooltip>
			<Menu
				anchorEl={anchor}
				open={anchor !== null}
				onClose={() => setAnchor(null)}
				data-component="environment-switcher-menu"
			>
				{names.length > 0 && [
					<Box key="names" sx={{ px: 2, py: 1, maxWidth: 420 }}>
						<Typography variant="caption" color="text.secondary">
							This instance answers on
						</Typography>
						{names.map((name) => (
							<Typography
								key={name}
								variant="body2"
								sx={{ fontFamily: "monospace" }}
							>
								{name}
							</Typography>
						))}
					</Box>,
					<Divider key="names-divider" />,
				]}
				{(["prod", "local", "staging"] as const).flatMap((group) => [
					<Box key={`${group}-title`} sx={{ px: 2, pt: 1 }}>
						<Typography variant="caption" color="text.secondary">
							{GROUP_TITLES[group]}
						</Typography>
					</Box>,
					...TARGETS.filter((t) => t.group === group).map((target) => (
						<MenuItem
							key={target.label}
							component="a"
							href={hrefFor(target.origin)}
							selected={target === current}
							data-component={`environment-option-${target.label.replace(
								" ",
								"-"
							)}`}
						>
							<ListItemText
								primary={target.label}
								secondary={target.origin.replace(/^https?:\/\//, "")}
								slotProps={{
									secondary: { sx: { fontFamily: "monospace" } },
								}}
							/>
						</MenuItem>
					)),
				])}
			</Menu>
		</>
	);
}
